/**
 * Cylinder cross-section fitting. The axis comes from the normal-covariance
 * eigen analysis (`estimateNormals`); this module fits the *radius and centre*
 * by projecting points onto the plane perpendicular to the axis and fitting a
 * circle there with the **Taubin** algebraic method.
 *
 * Why Taubin and not the simpler Kåsa fit: Kåsa minimises an algebraic error
 * that is heavily biased when only a partial arc is observed — it systematically
 * underestimates the radius of a short arc. Taubin's normalised cost is very
 * nearly unbiased on arcs, which is precisely our buried/occluded case. This is
 * the standard "CircleFitByTaubin" (Chernov) routine.
 */

import { dot, normalize, planeBasis, sub } from './linalg';
import type { Vec3 } from './types';

/** Axial coordinate of `p` along the line (axisPoint, axis-unit). */
export function axialCoord(p: Vec3, axisPoint: Vec3, axis: Vec3): number {
  return dot(sub(p, axisPoint), axis);
}

/** Perpendicular distance from `p` to the line (axisPoint, axis-unit). */
export function radialDistance(p: Vec3, axisPoint: Vec3, axis: Vec3): number {
  const d = sub(p, axisPoint);
  const t = dot(d, axis);
  const rx = d[0] - t * axis[0];
  const ry = d[1] - t * axis[1];
  const rz = d[2] - t * axis[2];
  return Math.sqrt(rx * rx + ry * ry + rz * rz);
}

export interface CircleFit2D {
  /** Circle centre in (u,v) plane coords relative to the projection origin. */
  uc: number;
  vc: number;
  radius: number;
  ok: boolean;
}

/**
 * Taubin algebraic circle fit. `u`/`v` hold the projected 2-D coordinates of
 * the first `n` points. Returns `ok:false` for degenerate input (collinear /
 * too few points / non-convergent).
 */
export function taubinCircleFit(u: Float64Array, v: Float64Array, n: number): CircleFit2D {
  if (n < 3) return { uc: 0, vc: 0, radius: 0, ok: false };

  let meanU = 0;
  let meanV = 0;
  for (let i = 0; i < n; i++) {
    meanU += u[i];
    meanV += v[i];
  }
  meanU /= n;
  meanV /= n;

  let Muu = 0;
  let Mvv = 0;
  let Muv = 0;
  let Muz = 0;
  let Mvz = 0;
  let Mzz = 0;
  for (let i = 0; i < n; i++) {
    const Ui = u[i] - meanU;
    const Vi = v[i] - meanV;
    const Zi = Ui * Ui + Vi * Vi;
    Muu += Ui * Ui;
    Mvv += Vi * Vi;
    Muv += Ui * Vi;
    Muz += Ui * Zi;
    Mvz += Vi * Zi;
    Mzz += Zi * Zi;
  }
  Muu /= n;
  Mvv /= n;
  Muv /= n;
  Muz /= n;
  Mvz /= n;
  Mzz /= n;

  const Mz = Muu + Mvv;
  const CovUv = Muu * Mvv - Muv * Muv;
  const VarZ = Mzz - Mz * Mz;

  const A3 = 4 * Mz;
  const A2 = -3 * Mz * Mz - Mzz;
  const A1 = VarZ * Mz + 4 * CovUv * Mz - Muz * Muz - Mvz * Mvz;
  const A0 = Muz * (Muz * Mvv - Mvz * Muv) + Mvz * (Mvz * Muu - Muz * Muv) - VarZ * CovUv;
  const A22 = A2 + A2;
  const A33 = A3 + A3 + A3;

  // Newton's method from x=0 for the smallest non-negative root of the
  // characteristic polynomial (Chernov's formulation).
  let x = 0;
  let y = A0;
  for (let iter = 0; iter < 99; iter++) {
    const Dy = A1 + x * (A22 + x * A33);
    const xnew = x - y / Dy;
    if (xnew === x || !Number.isFinite(xnew)) break;
    const ynew = A0 + xnew * (A1 + xnew * (A2 + xnew * A3));
    if (Math.abs(ynew) >= Math.abs(y)) break;
    x = xnew;
    y = ynew;
  }

  const det = x * x - x * Mz + CovUv;
  if (Math.abs(det) < 1e-20) return { uc: 0, vc: 0, radius: 0, ok: false };

  const ucCentred = (Muz * (Mvv - x) - Mvz * Muv) / det / 2;
  const vcCentred = (Mvz * (Muu - x) - Muz * Muv) / det / 2;
  const radius = Math.sqrt(ucCentred * ucCentred + vcCentred * vcCentred + Mz);
  if (!Number.isFinite(radius) || radius <= 0) return { uc: 0, vc: 0, radius: 0, ok: false };

  return { uc: ucCentred + meanU, vc: vcCentred + meanV, radius, ok: true };
}

export interface CrossSectionFit {
  center: Vec3;
  radius: number;
  rmsM: number;
  arcCoverageDeg: number;
  ok: boolean;
}

/**
 * Fit the pipe cross-section: project the given points (all points when
 * `indices` is null) onto the plane ⟂ axis through `refPoint`, run the Taubin
 * circle fit, lift the centre back to 3-D, and measure radial RMS + angular
 * coverage of the observed arc.
 */
export function fitCrossSection(
  positions: Float64Array,
  indices: number[] | null,
  totalCount: number,
  axis: Vec3,
  refPoint: Vec3,
): CrossSectionFit {
  const a = normalize(axis);
  const { e1, e2 } = planeBasis(a);
  const n = indices ? indices.length : totalCount;
  if (n < 3) return { center: refPoint, radius: 0, rmsM: Infinity, arcCoverageDeg: 0, ok: false };

  const u = new Float64Array(n);
  const v = new Float64Array(n);
  for (let k = 0; k < n; k++) {
    const i = indices ? indices[k] : k;
    const dx = positions[3 * i] - refPoint[0];
    const dy = positions[3 * i + 1] - refPoint[1];
    const dz = positions[3 * i + 2] - refPoint[2];
    u[k] = dx * e1[0] + dy * e1[1] + dz * e1[2];
    v[k] = dx * e2[0] + dy * e2[1] + dz * e2[2];
  }

  const circle = taubinCircleFit(u, v, n);
  if (!circle.ok) return { center: refPoint, radius: 0, rmsM: Infinity, arcCoverageDeg: 0, ok: false };

  // Lift centre back to 3-D.
  const center: Vec3 = [
    refPoint[0] + circle.uc * e1[0] + circle.vc * e2[0],
    refPoint[1] + circle.uc * e1[1] + circle.vc * e2[1],
    refPoint[2] + circle.uc * e1[2] + circle.vc * e2[2],
  ];

  // Radial RMS + arc coverage (5° bins).
  const BIN_DEG = 5;
  const nbins = Math.ceil(360 / BIN_DEG);
  const occupied = new Uint8Array(nbins);
  let sse = 0;
  for (let k = 0; k < n; k++) {
    const du = u[k] - circle.uc;
    const dv = v[k] - circle.vc;
    const rr = Math.sqrt(du * du + dv * dv);
    const resid = rr - circle.radius;
    sse += resid * resid;
    let deg = (Math.atan2(dv, du) * 180) / Math.PI;
    if (deg < 0) deg += 360;
    occupied[Math.min(nbins - 1, Math.floor(deg / BIN_DEG))] = 1;
  }
  let occ = 0;
  for (let b = 0; b < nbins; b++) occ += occupied[b];

  return {
    center,
    radius: circle.radius,
    rmsM: Math.sqrt(sse / n),
    arcCoverageDeg: occ * BIN_DEG,
    ok: true,
  };
}
